import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { parsePhoneNumber } from 'awesome-phonenumber';
import { TextField } from '@mui/material';
import { LoginDialogWidget, LoginDialogWidgetState, WidgetEditUnsuppressProvider, useWidgetConfig, useThemeFonts, PromptLoginWidget as SignInPromptWidget } from '@brandclub/bc-widgets';
import type { ThemeFontValue } from '@brandclub/bc-widgets';
import { useConciergeContext } from '../../ConciergeContextProvider';
import { useAppConfig } from '../../../api/cache/appConfigQueryOptions';
import { ResponsiveDialog } from '../../../Components/Dialog';
import { getBrandLogo } from '../../../utils/assetUtils';
import { isBackRoom } from '../../../utils/domain';

/** Resolve a fontFamily widget key to a CSS font-family string. */
function resolveFontFamilyCss(fontKey: string | undefined, themeFonts: Record<string, ThemeFontValue>): string | undefined {
  if (!fontKey || fontKey === 'inherit') return undefined;
  const fontData = themeFonts[fontKey];
  if (!fontData) return undefined;
  return fontData.format === 'system' ? fontData.url : `"${fontData.name}", sans-serif`;
}

/** Load a Google Font into document.head if needed. */
function loadGoogleFont(url: string) {
  if (typeof document === 'undefined') return;
  if (document.querySelector(`link[href="${url}"]`)) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = url;
  document.head.appendChild(link);
}

type LoginStep = 'phone' | 'otp' | 'loading';

/** Validate using awesome-phonenumber — same library as the main sign-in flow. */
const isPhoneValid = (phone: string) => parsePhoneNumber(phone, { regionCode: 'US' }).valid;

/**
 * Progressive phone mask matching getPhoneNumberMasking from landing-page StringUtils.
 * Strips formatting, then re-applies (XXX) XXX-XXXX progressively as digits are entered.
 */
const applyPhoneMask = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

/** Extract raw digits from a formatted phone string. */
const toDigits = (value: string) => value.replace(/\D/g, '');

/**
 * Wraps children in a div whose height animates whenever the content's
 * natural height changes. Uses ResizeObserver + CSS transition with no
 * extra dependencies.
 */
function AnimatedHeight({ children }: { children: React.ReactNode }) {
  const outerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const initialized = useRef(false);

  useLayoutEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return;

    const setHeight = (animated: boolean) => {
      const h = inner.offsetHeight;
      if (!animated) {
        outer.style.transition = 'none';
        outer.style.height = `${h}px`;
        // Force reflow so the transition kicks in on subsequent changes
        void outer.offsetHeight;
        outer.style.transition = 'height 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
        initialized.current = true;
      } else {
        outer.style.height = `${h}px`;
      }
    };

    const observer = new ResizeObserver(() => setHeight(initialized.current));
    observer.observe(inner);
    setHeight(false);

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={outerRef} style={{ overflow: 'hidden' }}>
      <div ref={innerRef}>{children}</div>
    </div>
  );
}


const Spinner: React.FC<{ size?: number; color?: string; trackColor?: string }> = ({
  size = 48,
  color = '#0D0D0D',
  trackColor = '#E0E0E0',
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 48 48"
    fill="none"
    style={{ animation: 'login-dialog-spin 0.9s linear infinite', flexShrink: 0 }}
  >
    <circle cx="24" cy="24" r="18" stroke={trackColor} strokeWidth="5" />
    <path
      d="M42 24A18 18 0 0 0 24 6"
      stroke={color}
      strokeWidth="5"
      strokeLinecap="round"
    />
  </svg>
);

type ResolveColorFn = Parameters<React.ComponentProps<typeof LoginDialogWidget>['children']>[1];

function LoginModalContent({
  onClose,
  brandLogoUrl,
  styles,
  resolveColor,
}: {
  onClose: () => void;
  brandLogoUrl?: string;
  styles: LoginDialogWidgetState;
  resolveColor: ResolveColorFn;
}) {
  const { inlineSignIn, inlineVerifyOtp } = useConciergeContext();
  const [logoFailed, setLogoFailed] = useState(false);
  const [step, setStep] = useState<LoginStep>('phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [session, setSession] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const phoneInputRef = useRef<HTMLInputElement>(null);
  const otpInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setTimeout(() => setResendCooldown((v) => v - 1), 1000);
    return () => clearTimeout(id);
  }, [resendCooldown]);

  useEffect(() => {
    if (step === 'phone') phoneInputRef.current?.focus();
    if (step === 'otp') otpInputRef.current?.focus();
  }, [step]);

  const submitPhone = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!inlineSignIn || step !== 'phone' || submitting) return;
    setError(null);
    setSubmitting(true);
    const e164 = parsePhoneNumber(phone, { regionCode: 'US' }).number?.e164 ?? phone;
    const res = await inlineSignIn(e164);
    setSubmitting(false);
    if ('error' in res) {
      setError(res.error);
    } else {
      setSession(res.session);
      setStep('otp');
      setResendCooldown(30);
    }
  };

  const verifyOtp = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!inlineVerifyOtp || step !== 'otp') return;
    setError(null);
    setStep('loading');
    const res = await inlineVerifyOtp(session, otp);
    if (res.success) {
      onClose();
    } else {
      setStep('otp');
      setError(res.error);
    }
  };

  const resend = async () => {
    if (!inlineSignIn || resendCooldown > 0) return;
    setError(null);
    const e164 = parsePhoneNumber(phone, { regionCode: 'US' }).number?.e164 ?? phone;
    const res = await inlineSignIn(e164);
    if ('error' in res) {
      setError(res.error);
    } else {
      setSession(res.session);
      setResendCooldown(30);
      setOtp('');
    }
  };

  const titleColor = resolveColor(styles.titleColor);
  const subtitleColor = resolveColor(styles.subtitleColor);

  if (step === 'loading') {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 16,
          padding: styles.dialogPadding,
          textAlign: 'center',
        }}
      >
        <p style={{ fontSize: styles.titleFontSize, fontWeight: 600, color: titleColor, margin: 0, lineHeight: '25px' }}>
          Almost there!
        </p>
        <p style={{ fontSize: styles.subtitleFontSize, fontWeight: 500, color: subtitleColor, margin: 0, lineHeight: '1.5', maxWidth: 280 }}>
          We&apos;re verifying your information, it will only take a second.
        </p>
        <Spinner size={48} color={titleColor} trackColor="rgba(0,0,0,0.1)" />
      </div>
    );
  }

  const isPhone = step === 'phone';
  const canContinue = (isPhone ? isPhoneValid(phone) : otp.length > 0) && !submitting;
  const title = isPhone ? 'Welcome' : "Just confirming it's you";
  const subtitle = isPhone
    ? 'Enter your phone number to continue. New here? We’ll create an account.'
    : `Please enter the code we sent to ${applyPhoneMask(phone)}.`;

  const primaryBg = resolveColor(styles.primaryButtonBgColor);
  const primaryText = resolveColor(styles.primaryButtonTextColor);
  const resendDisabled = resendCooldown > 0;
  const secondaryBorder = resolveColor(
    resendDisabled ? styles.secondaryButtonDisabledBorderColor : styles.secondaryButtonBorderColor
  );
  const secondaryText = resolveColor(styles.secondaryButtonTextColor);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        padding: styles.dialogPadding,
        boxSizing: 'border-box',
      }}
    >
      {/* Title + Description */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
        {/* Logo + title grouped together with 24px internal gap, matching Figma layout */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24 }}>
          {/* Brand logo circle */}
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: 0,
              backgroundColor: resolveColor(styles.logoContainerBgColor),
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {brandLogoUrl && !logoFailed && (
              <img
                src={brandLogoUrl}
                alt=""
                // Same reason as the sidebar: the URL is constructed, not
                // verified, so an empty circle beats a broken-image icon.
                onError={() => setLogoFailed(true)}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                }}
              />
            )}
          </div>

          <p style={{ fontSize: styles.titleFontSize, fontWeight: 600, color: titleColor, margin: 0, textAlign: 'center', lineHeight: '25px' }}>
            {title}
          </p>
        </div>

        <p style={{ fontSize: styles.subtitleFontSize, fontWeight: 500, color: subtitleColor, margin: 0, textAlign: 'center', lineHeight: '1.5', maxWidth: 320 }}>
          {subtitle}
        </p>

        {/* Input — styled to match StoreInput (MUI filled TextField with floating label) */}
        <TextField
          variant="filled"
          fullWidth
          label={isPhone ? 'Phone number' : 'Verification code'}
          value={isPhone ? applyPhoneMask(phone) : otp}
          type={isPhone ? 'tel' : 'text'}
          inputProps={{ inputMode: isPhone ? 'tel' : 'numeric', maxLength: isPhone ? undefined : 6 }}
          inputRef={isPhone ? phoneInputRef : otpInputRef}
          disabled={submitting}
          onChange={
            isPhone
              ? (e) => { setPhone(toDigits(e.target.value).slice(0, 10)); setError(null); }
              : (e) => { setOtp(e.target.value.replace(/\D/g, '')); setError(null); }
          }
          onKeyDown={(e) => { if (e.key === 'Enter') isPhone ? submitPhone() : verifyOtp(); }}
          slotProps={{ input: { disableUnderline: true } as any }}
          sx={{
            '.MuiInputLabel-root': {
              fontSize: 16,
              fontWeight: 500,
              color: titleColor,
              opacity: 0.5,
              '&.Mui-focused': { color: titleColor, opacity: 0.5 },
              '&.Mui-disabled': { color: titleColor },
            },
            '.MuiFilledInput-root': {
              backgroundColor: 'transparent',
              '&:hover': { backgroundColor: 'transparent' },
              '&.Mui-focused': { backgroundColor: 'transparent' },
              '&.Mui-disabled': { backgroundColor: 'transparent' },
              border: `1px solid ${resolveColor(styles.inputBorderColor)}`,
              borderRadius: `${styles.inputBorderRadius}px`,
              fontSize: 14,
              fontWeight: 500,
              color: titleColor,
              boxSizing: 'border-box',
              minHeight: 60,
              // Override browser autofill blue background
              '.MuiInputBase-input': {
                '&:-webkit-autofill': {
                  WebkitBoxShadow: `0 0 0 100px ${resolveColor(styles.dialogBgColor)} inset`,
                  WebkitTextFillColor: titleColor,
                  fontSize: '14px',
                },
              },
            },
          }}
        />
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center' }}>
        {error && (
          <p style={{ width: '100%', margin: 0, fontSize: 12, color: '#dc2626', padding: '4px 8px', backgroundColor: '#fef2f2', borderRadius: 6, lineHeight: 1.4, boxSizing: 'border-box' as const }}>
            {error}
          </p>
        )}

        <button
          type="button"
          disabled={!canContinue}
          onClick={isPhone ? submitPhone : verifyOtp}
          style={{
            width: '100%',
            padding: '13px 16px',
            borderRadius: styles.primaryButtonBorderRadius,
            border: 'none',
            fontSize: 14,
            fontWeight: 600,
            lineHeight: '20px',
            backgroundColor: primaryBg,
            color: primaryText,
            opacity: !canContinue ? 0.3 : 1,
            cursor: !canContinue ? 'default' : 'pointer',
            pointerEvents: !canContinue ? 'none' : 'auto',
            transition: 'background-color 0.15s ease, opacity 0.15s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
          }}
        >
          {submitting ? (
            <>
              <Spinner size={18} color={primaryText} trackColor="rgba(255,255,255,0.35)" />
              Sending code…
            </>
          ) : (
            'Continue'
          )}
        </button>

        {!isPhone && (
          <button
            type="button"
            disabled={resendDisabled}
            onClick={resend}
            style={{
              width: '100%',
              padding: '13px 16px',
              borderRadius: styles.secondaryButtonBorderRadius,
              border: `1.5px solid ${secondaryBorder}`,
              fontSize: 14,
              fontWeight: 600,
              lineHeight: '20px',
              backgroundColor: 'transparent',
              color: resendDisabled ? resolveColor(styles.secondaryButtonDisabledBorderColor) : secondaryText,
              cursor: resendDisabled ? 'default' : 'pointer',
              transition: 'all 0.15s ease',
            }}
          >
            {resendDisabled
              ? `Resend code in 0:${String(resendCooldown).padStart(2, '0')}`
              : 'Resend code'}
          </button>
        )}

        <button
          type="button"
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            fontSize: 14,
            fontWeight: 600,
            lineHeight: '20px',
            color: resolveColor(styles.cancelTextColor),
            cursor: 'pointer',
          }}
        >
          Back
        </button>
      </div>
    </div>
  );
}

export function PromptLoginPart({
  reason,
  loginMethod,
  isStreaming,
}: {
  reason?: string;
  loginMethod?: 'shopify_link' | 'phone_otp';
  isStreaming?: boolean;
}) {
  const { isSignedIn, inlineSignIn, inlineVerifyOtp, isSharedView, requestSignIn } = useConciergeContext();
  const { data: appConfig } = useAppConfig();
  const [open, setOpen] = useState(!isStreaming);

  // Plan 7 C3 — Shopify surface: an inline sign-in CTA that runs the
  // federated Shopify login (via the host-injected `requestSignIn`, which
  // clears the D-18 debounce and honors the D-19 return trip). Double-gated
  // on purpose: the server's `loginMethod` is authoritative, and the
  // client-side `isBackRoom()` check guarantees no phone/OTP UI ever renders
  // on-store even before the surface-aware backend deploys.
  //
  // D-42: the app's auth TYPE is the real answer — a `shopify` app always uses
  // the store's login, and a `stackline` app must keep its phone/OTP form even
  // when it happens to be served on a Shopify domain. `isBackRoom()` stays as a
  // fallback for bundles served by a backend that predates `authType`.
  const useShopifyLink =
    appConfig?.authType === 'shopify' ||
    (appConfig?.authType === undefined && (loginMethod === 'shopify_link' || isBackRoom()));

  useEffect(() => {
    if (!isStreaming && !open) setOpen(true);
  }, [isStreaming]); // eslint-disable-line react-hooks/exhaustive-deps

  const brandId = appConfig?.domainConfig?.brandId;
  const brandLogoUrl = brandId ? getBrandLogo(brandId) : undefined;

  // Follow the brand font configured for the home-page chat input.
  const homeInputConfig = useWidgetConfig<{ fontFamily?: string }>('concierge-chat-input', {});
  const { themeFonts } = useThemeFonts();
  const fontFamilyCss = resolveFontFamilyCss(homeInputConfig.fontFamily, themeFonts);
  const fontData = homeInputConfig.fontFamily && homeInputConfig.fontFamily !== 'inherit'
    ? themeFonts[homeInputConfig.fontFamily]
    : null;
  useEffect(() => {
    if (fontData?.format === 'google' && fontData.url) {
      loadGoogleFont(fontData.url);
    }
  }, [fontData?.url]);

  if (isSignedIn || isSharedView) return null;

  // The phone/OTP form can only run where the host injected `inlineSignIn` /
  // `inlineVerifyOtp` — today that is the landing-page bundle alone. The EMBED
  // bubble mounts the same concierge without them, and this used to `return
  // null`, so a signed-out shopper who asked the bubble about an order got the
  // model's "sign in here" with nothing to click and no way forward. Fall back
  // to the same CTA the Shopify branch uses: hand off to the surface that can
  // sign them in.
  const cannotRunInlineForm = !inlineSignIn || !inlineVerifyOtp;

  if (useShopifyLink || cannotRunInlineForm) {
    // Inline CTA in the message flow — never a modal, never a phone field.
    // With no destination there is nothing to offer, and a button that does
    // nothing is worse than no button.
    if (!requestSignIn) return null;
    const target = reason?.trim();
    const description = useShopifyLink
      ? target
        ? `Sign in with your store account to ${target}.`
        : 'Sign in with your store account to continue.'
      : target
        ? `Sign in to ${target}.`
        : 'Sign in to continue.';
    return (
      <div
        // Centred in the message column, with breathing room before the
        // assistant's text that follows. Left-aligned and flush against the
        // next paragraph, this read as a stray form rather than a prompt.
        style={{
          maxWidth: 400,
          margin: '0 auto 20px',
          ...(fontFamilyCss ? { fontFamily: fontFamilyCss } : {}),
        }}
      >
        <WidgetEditUnsuppressProvider>
          <SignInPromptWidget
            widgetId="concierge-prompt-login"
            title="Sign in to continue"
            description={description}
            buttonText="Sign in"
            onSignIn={() => requestSignIn()}
          />
        </WidgetEditUnsuppressProvider>
      </div>
    );
  }

  if (!open) return null;

  return (
    <>
      <style>{`
        @keyframes login-dialog-spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }
        @media (max-width: 899.95px) { .login-dialog-content { border-bottom-left-radius: 0 !important; border-bottom-right-radius: 0 !important; } }
      `}</style>
      <ResponsiveDialog
        open={open}
        onClose={((_event: object, reason: 'backdropClick' | 'escapeKeyDown') => { if (reason !== 'backdropClick') setOpen(false); }) as () => void}
        disableScrollLock
        sx={(theme) => ({
          zIndex: 10000,
          '& div.MuiDialog-paper': {
            margin: '0 20px',
            width: '100%',
            maxWidth: 400,
            overflow: 'visible',
            backgroundColor: 'transparent',
            boxShadow: 'none',
            paddingTop: 0,
            [theme.breakpoints.down('md')]: {
              maxWidth: '100%',
              width: '100%',
              margin: 0,
              padding: 0,
              paddingTop: '12px',
              boxSizing: 'border-box',
            },
          },
        })}
      >
        <WidgetEditUnsuppressProvider>
          <LoginDialogWidget
            widgetId="concierge-login-dialog"
            displayName="Login Dialog"
          >
            {(styles, resolveColor, resolvedLogoUrl) => (
              <div
                className="login-dialog-content"
                style={{
                  backgroundColor: resolveColor(styles.dialogBgColor),
                  borderRadius: styles.dialogBorderRadius,
                  boxShadow: '8px 8px 14px 0px rgba(0,0,0,0.03)',
                  border: '1px solid rgba(13,13,13,0.05)',
                  overflow: 'hidden',
                  ...(fontFamilyCss ? { fontFamily: fontFamilyCss } : {}),
                }}
              >
                <AnimatedHeight>
                  <LoginModalContent
                    onClose={() => setOpen(false)}
                    brandLogoUrl={resolvedLogoUrl ?? brandLogoUrl}
                    styles={styles}
                    resolveColor={resolveColor}
                  />
                </AnimatedHeight>
              </div>
            )}
          </LoginDialogWidget>
        </WidgetEditUnsuppressProvider>
      </ResponsiveDialog>
    </>
  );
}
